import React from 'react';
import {RadialGraphProps} from './RadialGraph.props';
import './RadialGraph.styles.css';
import {pieArcClasses,  PieChart } from '@mui/x-charts/PieChart';
import { styled } from '@mui/material/styles';
import {useDrawingArea} from "@mui/x-charts";
import {Tooltip} from "@mui/material";
import Box from "@mui/material/Box";
import {EXCEPTION_CATEGORY_NAMES} from "../../../constants/contants";

const HEADER_RULE_PRESENTATIONS = {
    clean: {
        color: "#03A9F5",
        tooltipValue: "No threat",
        toolTipDescription: "No threat detected"
    },
    undetermined: {
        color: "#FCAB10",
        tooltipValue: "Undetermined",
        toolTipDescription: "Unknown threat. Or email scanning failed due to multiple factors"
    },
    threat: {
        color: "#ED1C24",
        tooltipValue: "Threat detected",
        toolTipDescription: "High confidence in threat detected"
    }
};

export const getRiskPresentation = (value: number, headerRuleCategory?: string) => {
    const categoryKey = typeof headerRuleCategory === 'string'
        ? headerRuleCategory.trim().toLowerCase()
        : '';
    if (categoryKey in HEADER_RULE_PRESENTATIONS) {
        return HEADER_RULE_PRESENTATIONS[categoryKey as keyof typeof HEADER_RULE_PRESENTATIONS];
    }

    if (value >= 70) {
        return {
            color: "#ED1C24",
            tooltipValue: "Threat detected",
            toolTipDescription: "High confidence in threat detected"
        };
    }

    if (value >= 40) {
        return {
            color: "#FCAB10",
            tooltipValue: "Undetermined",
            toolTipDescription: "Unknown threat. Or email scanning failed due to multiple factors"
        };
    }

    return {
        color: "#03A9F5",
        tooltipValue: "No threat",
        toolTipDescription: "No threat detected"
    };
};

type AuthScoreTooltipDetails = {
    ruleName: string;
    categoryName: string;
};

/**
 * Attribution was not present on historic scan results. Only use the
 * attribution-specific tooltip when every value required to explain the rule
 * is available and trustworthy.
 */
export const getAuthScoreTooltipDetails = (attribution: unknown): AuthScoreTooltipDetails | undefined => {
    if (!attribution || typeof attribution !== 'object' || Array.isArray(attribution)) {
        return undefined;
    }

    const {applied_by: appliedBy, rule} = attribution as {
        applied_by?: unknown;
        rule?: unknown;
    };
    if (appliedBy !== 'Threat Score Rules' || !rule || typeof rule !== 'object' || Array.isArray(rule)) {
        return undefined;
    }

    const {name, category} = rule as {name?: unknown; category?: unknown};
    if (typeof name !== 'string' || !name.trim() || typeof category !== 'string') {
        return undefined;
    }

    const categoryKey = category.trim().toLowerCase() as keyof typeof EXCEPTION_CATEGORY_NAMES;
    const categoryName = EXCEPTION_CATEGORY_NAMES[categoryKey];
    return categoryName ? {ruleName: name, categoryName} : undefined;
};

export const RadialGraph: React.FC<RadialGraphProps> = ({
    value,
    innerCircle = 16,
    outerCircle = 20,
    chartStartPoint = 0,
    chartLayoutPosition = {left: 10},
    isSkipAnimation = true,
    chartStyle = {},
    labelSize = 10,
    labelWeight = 700,
    labelFamily = 'Montserrat',
    labelColor = '#4D5858',
    isToolTipHover = false,
    prefixValue ='',
    headerRuleCategory,
    authScoreAttribution
}) => {
    const riskPresentation = getRiskPresentation(value, headerRuleCategory);
    const authScoreTooltipDetails = getAuthScoreTooltipDetails(authScoreAttribution);

    const chartEndPoint = chartStartPoint + (3.6 * value)
  const StyledText = styled('text')(({ theme }) => ({
    fill: labelColor,
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize: labelSize,
    fontWeight: labelWeight,
    fontFamily: labelFamily,
    cursor: isToolTipHover ? "pointer" : "default"
  }));

  const toolTipComponent = () => {
      if (authScoreTooltipDetails) {
          return <Box component="ul" sx={{m: 0, pl: 2.5}}>
              <li>Applied by: Threat Score Rules</li>
              <li>Rule applied: {authScoreTooltipDetails.ruleName} - {authScoreTooltipDetails.categoryName}</li>
          </Box>;
      }

        return <Box sx={{
            fontFamily: 'Montserrat',
            fontSize: '13px',
            lineHeight: 'normal',
            color: '#FFF'
        }}>
            <span style={{display: 'flex', justifyContent: 'center', fontWeight: 700}}>{riskPresentation.tooltipValue}</span>
            <span style={{fontWeight: 500}}>{riskPresentation.toolTipDescription}</span>
        </Box>
    }

  const PieCenterLabel = ({ children }: { children: React.ReactNode }) => {
    const { width, height, left, top } = useDrawingArea();
    const textLabel = <StyledText x={left + width / 2} y={top + height / 2}>
        {children}
    </StyledText>
  return isToolTipHover ? <Tooltip placement={"top"} title={toolTipComponent()} componentsProps={{tooltip: {sx:{maxWidth: "450px"}} }}>
          {textLabel}
      </Tooltip> : textLabel
  }

  const data = [
    {
      startAngle: chartStartPoint,
      endAngle: 360,
      paddingAngle: 0,
      innerRadius: innerCircle,
      outerRadius: outerCircle,
      data: [{value: 100, color: '#f2f3f3'}]
    },
    {
      startAngle: chartStartPoint,
      endAngle: chartEndPoint,
      innerRadius: innerCircle,
      outerRadius: outerCircle,
      data: [{value: value, color: riskPresentation.color}],
    },
  ]

  return (
      <PieChart
          series={data}
          sx={{[`& .${pieArcClasses.root}`]: chartStyle }}
          skipAnimation={isSkipAnimation}
          tooltip={{ trigger: 'none' }}
          slotProps={{legend: { hidden: true } }}
          margin={chartLayoutPosition}
      >
        <PieCenterLabel>{value}{prefixValue}</PieCenterLabel>
      </PieChart>
  );
};
